import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { timeout, map, catchError, throwError, firstValueFrom } from 'rxjs';

import { ConfigService } from './config.service';
import { IAudit } from '@models/types';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private readonly TIMEOUT = 15000;

	constructor(
		private configService: ConfigService,
		private httpClient: HttpClient,
	) { }

	public async validateCountry(): Promise<any> {
		const url = this.configService.restApiUrl + '/country-validation';
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		};

		try {
			const response = await firstValueFrom(
				this.httpClient.get(url, httpOptions).pipe(
					timeout(this.TIMEOUT),
					map(res => res)
				)
			);
			return response;
		} catch (error) {
			// throw the error to propagate HTTP errors properly
			throw error;
		}
	}

	public async getForm() {
		const url = this.configService.restApiUrl + '/form';
		const body: any = {
			// email,
			// password
		};
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		};
		return this.httpClient.post(url, body, httpOptions).pipe(timeout(this.TIMEOUT), map(response => response as any), catchError(e => {
			if (e.status === 401) {
				return throwError(() => new Error(e.error));
			}
			return this.handleError(e);
		})).toPromise();
	}

	public async submitForm(audit: IAudit, name: string, email: string, addToMailingList: boolean) {
		const url = this.configService.restApiUrl + '/submission';
		const body: any = {
			audit,
			name,
			email,
			add_to_mailing_list: addToMailingList
		};
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		};
		return this.httpClient.post(url, body, httpOptions).pipe(timeout(this.TIMEOUT), map(response => response as any), catchError(e => {
			if (e.status === 401) {
				return throwError(() => new Error(e.error));
			}
			return this.handleError(e);
		})).toPromise();
	}

	public async validate(uuid: string): Promise<{ updated: boolean; name: string; audit: IAudit }> {
		const url = this.configService.restApiUrl + '/validate/' + uuid;
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		};
		return this.httpClient.get(url, httpOptions).pipe(timeout(this.TIMEOUT), map(response => response as any), catchError(e => {
			if (e.status === 401) {
				return throwError(() => new Error(e.error));
			}
			return this.handleError(e);
		})).toPromise();
	}

	public async getCumulativeChartData(): Promise<number[]> {
		const url = this.configService.restApiUrl + '/get-cumulative-data';
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		};
		return this.httpClient.get(url, httpOptions).pipe(timeout(this.TIMEOUT), map(response => response as any), catchError(e => {
			if (e.status === 401) {
				return throwError(() => new Error(e.error));
			}
			return this.handleError(e);
		})).toPromise();
	}

	public getLocalImageFromAssets(fileName: string) {
		return new Promise((resolve, reject) => {
			this.httpClient.get('assets/TickAudit/images/' + fileName, { responseType: 'blob' }).pipe(timeout(5000))
				.subscribe((response: Blob) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						resolve(reader.result as string);
					};
					reader.readAsDataURL(response);
				}, (error) => {
					reject(error);
				});
		});
	}

	private handleError(error: any): any {
		if (error.status === 0) {
			return throwError(() => new Error('Cannot connect to the server at present. Please try again later'));
		} else if (error.name === 'TimeoutError') {
			return throwError(() => new Error('Unable to connect to server. Please check your internet settings and try again.'));
		} else {
			return throwError(() => new Error(error.error));
		}
	}
}
